/**
 * Convert a normalized location string back to a human-readable format.
 *
 * Rules:
 *   - Underscores become spaces
 *   - All-alpha words with 5 or fewer letters → UPPERCASE (country codes, airport/seaport codes)
 *   - Alphanumeric mix with fewer than 8 chars → UPPERCASE (postal codes like B37, SE1, 3HB)
 *   - Any other all-alpha word (6+ letters) → capitalize first letter only (city names, streets)
 *   - Purely numeric words are left as-is (postal codes: 8001, 06714)
 *
 * Examples:
 *   "main_st,_zurich,_8001,ch"  → "Main St, Zurich, 8001, CH"
 *   "zrh"                        → "ZRH"
 *   "deham"                      → "DEHAM"
 *   "b37_7hb"                    → "B37 7HB"
 *   "se1_3xy"                    → "SE1 3XY"
 */
export function displayLocation(normalized: string | null | undefined): string {
  if (!normalized) return '';

  return normalized
    .replaceAll('_', ' ')
    .replaceAll(/\b(\S+)\b/g, (word) => {
      const isAlphaOnly = /^[a-zA-Z]+$/.test(word);
      const isNumericOnly = /^\d+$/.test(word);
      const isAlphanumeric = /^[a-zA-Z0-9]+$/.test(word);

      // Purely numeric — leave as-is
      if (isNumericOnly) return word;

      // All-alpha, 5 chars or fewer — uppercase (country/airport/seaport codes)
      if (isAlphaOnly && word.length <= 5) return word.toUpperCase();

      // Alphanumeric mix, under 8 chars — uppercase (postal code fragments like B37, 7HB, SE1)
      if (isAlphanumeric && !isAlphaOnly && word.length < 8) return word.toUpperCase();

      // All-alpha, 6+ chars — capitalize first letter (city/street names)
      if (isAlphaOnly) return word.charAt(0).toUpperCase() + word.slice(1);

      // Anything else (special chars, hyphenated) — leave as-is
      return word;
    });
}

/**
 * Normalize a human-readable location string for storage.
 * Mirrors distancePersistenceService.normalizeLocation():
 *   .trim().toLowerCase().replace(/\s+/g, '_')
 */
export function normalizeLocation(display: string | null | undefined): string {
  if (!display) return '';
  return display.trim().toLowerCase().replaceAll(/\s+/g, '_');
}
